<template>
	<section class="collapsible">
		<button class="collapsible_toggle"
			type="button"
			:aria-expanded="String(expanded)"
			:aria-controls="bodyId"
			@click="toggle">
			<svg class="chevron" :class="{ 'is-collapsed': !expanded }"
				viewBox="0 0 20 20" fill="none" aria-hidden="true">
				<path d="M5 8l5 5 5-5" stroke="currentColor" stroke-width="1.5"
					stroke-linecap="round" stroke-linejoin="round" />
			</svg>
			<span class="toggle_label">{{ title }}</span>
			<span v-if="$slots.hint && !expanded" class="toggle_hint">
				<slot name="hint" />
			</span>
		</button>
		<transition name="collapsible_height"
			@enter="onEnter"
			@after-enter="onAfterEnter"
			@before-leave="onBeforeLeave"
			@leave="onLeave">
			<div v-show="expanded" :id="bodyId" class="collapsible_body">
				<slot />
			</div>
		</transition>
	</section>
</template>

<script>
// Sección colapsable genérica para los tabs de financialReports. Pensada
// como "modo foco" en pantallas analíticas: el header del toggle queda
// visible siempre (~32px) y el body se anima fuera del flow sin afectar a
// los hermanos que vienen abajo (ej. tabla de evolución).
export default {
	name: "CollapsibleSection",

	props: {
		title: { type: String, default: "Resumen" },
		// Si viene, el estado expandido/colapsado se persiste en localStorage
		// bajo esa key. Distinta por tab (p.ej. "ventas:summary-expanded")
		// para que cada reporte tenga su propia preferencia.
		storageKey: { type: String, default: "" },
		defaultExpanded: { type: Boolean, default: true },
	},

	data() {
		return {
			expanded: this.defaultExpanded,
			// _uid es estable per-instance en Vue 2 — sirve para vincular
			// aria-controls del button con el id del body.
			bodyId: `collapsible-body-${this._uid}`,
		};
	},

	created() {
		if (!this.storageKey) return;
		try {
			const saved = localStorage.getItem(this.storageKey);
			if (saved !== null) this.expanded = saved === "true";
		} catch (e) {
			// localStorage puede estar deshabilitado (modo privado, sandbox).
			// Falla silenciosa: nos quedamos con defaultExpanded.
		}
	},

	methods: {
		toggle() {
			this.expanded = !this.expanded;
			if (this.storageKey) {
				try {
					localStorage.setItem(this.storageKey, String(this.expanded));
				} catch (e) { /* noop */ }
			}
			this.$emit("update:expanded", this.expanded);
		},

		// Hooks JS de <transition>: animan max-height entre 0 y scrollHeight,
		// porque CSS no anima `auto`. Reseteamos los estilos inline al final
		// para no dejar max-height fijo (rompería el resize del contenido).
		onEnter(el) {
			el.style.maxHeight = "0";
			el.style.opacity = "0";
			el.style.overflow = "hidden";
			// Reflow forzado: el navegador aplica el frame inicial antes
			// de la transición — sin esto, salta directo al final.
			void el.offsetHeight;
			el.style.transition =
				"max-height 280ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease-out";
			el.style.maxHeight = `${el.scrollHeight}px`;
			el.style.opacity = "1";
		},
		onAfterEnter(el) {
			el.style.maxHeight = "";
			el.style.opacity = "";
			el.style.overflow = "";
			el.style.transition = "";
		},
		onBeforeLeave(el) {
			el.style.maxHeight = `${el.scrollHeight}px`;
			el.style.opacity = "1";
			el.style.overflow = "hidden";
		},
		onLeave(el) {
			void el.offsetHeight;
			el.style.transition =
				"max-height 240ms cubic-bezier(0.4, 0, 1, 1), opacity 160ms ease-in";
			el.style.maxHeight = "0";
			el.style.opacity = "0";
		},
	},
};
</script>

<style scoped lang="scss">
.collapsible {
	display: block;
}

// Toggle: chrome thin. Sin border ni shadow propios — la barra es transparent
// y solo gana background en hover/focus. Coherente con .filter_btn del header.
.collapsible_toggle {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 6px 10px;
	background: transparent;
	border: none;
	border-radius: 8px;
	color: var(--report-text-muted-strong);
	font-size: 13px;
	font-weight: 500;
	line-height: 20px;
	cursor: pointer;
	transition: background-color 160ms ease, color 160ms ease;

	&:hover {
		background: var(--report-surface);
		color: var(--report-text);
	}

	&:focus-visible {
		outline: 2px solid var(--report-accent-border);
		outline-offset: 2px;
	}
}

.chevron {
	width: 14px;
	height: 14px;
	flex-shrink: 0;
	// Deceleration easing — micro-interaction whisper, igual que el sistema.
	transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1);
}

.chevron.is-collapsed {
	transform: rotate(-90deg);
}

.toggle_label {
	letter-spacing: 0.01em;
}

.toggle_hint {
	color: var(--report-text-muted);
	font-size: 12px;
	font-weight: 400;
	margin-left: 4px;
	// Pipe sutil entre el title y el hint: estilo "breadcrumb metadata".
	&::before {
		content: "·";
		margin-right: 6px;
		opacity: 0.6;
	}
}

.collapsible_body {
	margin-top: 8px;
}
</style>
